import React from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import StatusIndicator from "components/ui/StatusIndicator";

export default function CoursePreviewModal({ course, onClose, onBook, volunteerStatus }) {
  if (!course) return null;
  const isEligible = course?.requiredStatus?.includes(volunteerStatus);
  const isFull = course?.enrolled >= course?.capacity;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Course preview: ${course?.title}`}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[var(--color-card)] rounded-2xl shadow-[var(--shadow-xl)] w-full max-w-2xl max-h-[90vh] overflow-y-auto z-10">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--color-card)] border-b border-[var(--color-border)] px-5 py-4 flex items-start justify-between gap-3 rounded-t-2xl z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[var(--color-primary)] flex items-center justify-center flex-shrink-0">
              <Icon name={course?.icon} size={20} color="#fff" />
            </div>
            <div className="min-w-0">
              <h2 className="font-heading font-bold text-base md:text-lg text-[var(--color-foreground)] line-clamp-2">
                {course?.title}
              </h2>
              <p className="text-xs text-[var(--color-muted-foreground)]">{course?.category} · {course?.format}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[var(--color-muted)] flex-shrink-0 transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: "Clock", label: "Duration", value: course?.duration },
              { icon: "Calendar", label: "Next Session", value: course?.nextSession },
              { icon: "User", label: "Instructor", value: course?.instructor },
              { icon: "Users", label: "Capacity", value: `${course?.enrolled}/${course?.capacity}` },
            ]?.map((item) => (
              <div key={item?.label} className="bg-[var(--color-muted)] rounded-lg p-3 text-center">
                <Icon name={item?.icon} size={16} color="var(--color-primary)" className="mx-auto mb-1" />
                <p className="text-xs text-[var(--color-muted-foreground)] mb-0.5">{item?.label}</p>
                <p className="text-xs font-semibold text-[var(--color-foreground)]">{item?.value}</p>
              </div>
            ))}
          </div>

          {/* Description */}
          <div>
            <h3 className="font-heading font-semibold text-sm text-[var(--color-foreground)] mb-2">About This Course</h3>
            <p className="text-sm text-[var(--color-muted-foreground)] leading-relaxed">{course?.fullDescription}</p>
          </div>

          {/* Syllabus */}
          <div>
            <h3 className="font-heading font-semibold text-sm text-[var(--color-foreground)] mb-2">Course Syllabus</h3>
            <div className="space-y-2">
              {course?.syllabus?.map((module, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-[var(--color-muted)] rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-[var(--color-primary)] text-white text-xs flex items-center justify-center flex-shrink-0 font-semibold">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--color-foreground)]">{module.title}</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">{module.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prerequisites */}
          {course?.prerequisites?.length > 0 && (
            <div>
              <h3 className="font-heading font-semibold text-sm text-[var(--color-foreground)] mb-2">Prerequisites</h3>
              <ul className="space-y-1">
                {course?.prerequisites?.map((p, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
                    <Icon name="CheckCircle2" size={14} color="var(--color-success)" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Status requirement */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[var(--color-muted-foreground)]">Required volunteer status:</span>
            {course?.requiredStatus?.map((s) => (
              <StatusIndicator key={s} status={s} size="sm" showTooltip />
            ))}
          </div>

          {!isEligible && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <Icon name="AlertTriangle" size={16} color="currentColor" />
              <span>You need to upgrade your volunteer status to enroll in this course.</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Close
            </Button>
            <Button
              variant="default"
              iconName="BookOpen"
              iconPosition="left"
              disabled={!isEligible || isFull}
              onClick={() => { onBook(course); onClose(); }}
              className="flex-1"
            >
              {isFull ? "Course Full" : !isEligible ? "Not Eligible" : "Book Course"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}